import React from 'react';
import { Alert } from 'antd';
import {
  CalendarOutlined,
  FallOutlined,
  FundProjectionScreenOutlined,
  ShoppingOutlined,
} from '@ant-design/icons';
import { formatReportMoneyFa, formatReportNumberFa } from '../../../pages/reports/chartFormatters';
import { toNumber } from '../../../pages/reports/reportUtils';

/**
 * نرمال‌سازی action_plan از API یا ساخت fallback از فیلدهای پراکنده
 */
export const resolveActionPlan = (source = {}, extras = {}) => {
  const plan = source?.action_plan || source?.month?.action_plan || extras.action_plan || null;
  if (plan && typeof plan === 'object') {
    const isBehind =
      plan.is_behind === true ||
      plan.status === 'behind_schedule' ||
      plan.status === 'needs_attention' ||
      plan.status === 'behind' ||
      plan.status === 'at_risk';
    if (plan.is_behind === false && !isBehind) return null;
    if (!isBehind && plan.is_behind == null) {
      const hasSignals =
        toNumber(plan.gap_revenue) > 0 ||
        toNumber(plan.daily_revenue_needed) > 0 ||
        toNumber(plan.deals_needed) > 0;
      if (!hasSignals) return null;
      // سیگنال عددی هست ولی فلگ صریح نیست — فقط وقتی فاصله مثبت داریم عقب‌مانده فرض می‌کنیم
      if (toNumber(plan.gap_revenue) <= 0) return null;
      return { ...plan, is_behind: true };
    }
    if (!isBehind) return null;
    return { ...plan, is_behind: true };
  }

  const gap = Math.max(
    0,
    toNumber(
      source.gap_revenue ??
        source.gap ??
        source.gap_amount ??
        extras.gap_revenue ??
        (toNumber(source.target ?? source.target_revenue) -
          toNumber(source.current ?? source.actual_revenue ?? source.actual)),
    ),
  );
  const workingDays = toNumber(
    source.working_days_remaining ??
      extras.working_days_remaining ??
      extras.period?.working_days_remaining ??
      extras.period?.days_remaining,
  );
  const dailyNeeded = toNumber(
    source.daily_revenue_needed ??
      source.daily_needed ??
      extras.daily_revenue_needed ??
      (gap > 0 && workingDays > 0 ? gap / workingDays : 0),
  );
  const avgDeal = toNumber(
    source.avg_deal_size ?? extras.avg_deal_size ?? extras.deal_size?.current_average,
  );
  const dealsNeeded = toNumber(
    source.deals_needed ?? extras.deals_needed ?? (gap > 0 && avgDeal > 0 ? Math.ceil(gap / avgDeal) : 0),
  );

  const status = String(
    source.target_status || source.status || extras.status || '',
  ).toLowerCase();
  const isBehind =
    status === 'behind_schedule' ||
    status === 'needs_attention' ||
    status === 'behind' ||
    status === 'at_risk' ||
    (gap > 0 && workingDays > 0 && dailyNeeded > 0);

  if (!isBehind || gap <= 0) return null;

  return {
    is_behind: true,
    status: status || 'behind_schedule',
    status_label: source.target_status_label || source.status_label || 'عقب‌تر از برنامه',
    gap_revenue: gap,
    gap_revenue_formatted: source.gap_revenue_formatted || formatReportMoneyFa(gap),
    gap_revenue_text: source.gap_revenue_text || `فاصله تا هدف: ${formatReportMoneyFa(gap)}`,
    working_days_remaining: workingDays || null,
    working_days_remaining_text:
      workingDays > 0
        ? source.working_days_remaining_text ||
          `روز کاری باقی‌مانده: ${formatReportNumberFa(workingDays)}`
        : null,
    daily_revenue_needed: dailyNeeded || null,
    daily_revenue_needed_formatted:
      dailyNeeded > 0
        ? source.daily_revenue_needed_formatted || formatReportMoneyFa(dailyNeeded)
        : null,
    daily_revenue_needed_text:
      dailyNeeded > 0
        ? source.daily_revenue_needed_text ||
          `فروش لازم روزانه: ${formatReportMoneyFa(dailyNeeded)}`
        : null,
    deals_needed: dealsNeeded || null,
    deals_needed_text:
      dealsNeeded > 0
        ? source.deals_needed_text ||
          `معاملات موردنیاز: حدود ${formatReportNumberFa(dealsNeeded)} معامله`
        : null,
    summary_text: source.summary_text || null,
  };
};

const ActionPlanBox = ({ icon, label, value, emphasize = false }) => (
  <div className="rounded-lg border border-rose-200/80 dark:border-rose-800/50 bg-white/70 dark:bg-slate-900/40 px-3 py-2.5 min-w-0">
    <div className="flex items-center gap-1.5 text-[10px] text-rose-700/80 dark:text-rose-300/80 mb-1">
      {icon}
      <span>{label}</span>
    </div>
    <div
      className={`tabular-nums leading-snug text-rose-900 dark:text-rose-100 break-words ${
        emphasize ? 'text-sm font-bold' : 'text-[13px] font-semibold'
      }`}
    >
      {value || '—'}
    </div>
  </div>
);

/**
 * کارت برنامه اقدام فوری (Goal → Action) وقتی از برنامه عقب هستیم
 */
export const GoalActionPlanCard = ({
  actionPlan,
  className = '',
  compact = false,
  title = 'برنامه اقدام برای جبران عقب‌ماندگی',
}) => {
  const plan = actionPlan;
  if (!plan?.is_behind) return null;

  const gapValue = plan.gap_revenue_formatted || formatReportMoneyFa(plan.gap_revenue);
  const daysValue =
    plan.working_days_remaining != null && plan.working_days_remaining > 0
      ? `${formatReportNumberFa(plan.working_days_remaining)} روز کاری`
      : null;
  const dailyValue =
    plan.daily_revenue_needed_formatted ||
    (plan.daily_revenue_needed > 0 ? formatReportMoneyFa(plan.daily_revenue_needed) : null);
  const dealsValue =
    plan.deals_needed_text?.replace(/^معاملات موردنیاز:\s*/i, '') ||
    (plan.deals_needed > 0 ? `حدود ${formatReportNumberFa(plan.deals_needed)} معامله` : null);

  if (!gapValue && !daysValue && !dailyValue && !dealsValue) return null;

  if (compact) {
    return (
      <Alert
        type="warning"
        showIcon
        className={`custom-font ${className}`}
        message={plan.status_label || title}
        description={
          <div className="text-[11px] leading-relaxed space-y-0.5">
            {gapValue ? <div>فاصله تا هدف: {gapValue}</div> : null}
            {daysValue ? <div>روز کاری باقی‌مانده: {daysValue}</div> : null}
            {dailyValue ? <div>فروش لازم روزانه: {dailyValue}</div> : null}
            {dealsValue ? <div>معاملات موردنیاز: {dealsValue}</div> : null}
          </div>
        }
      />
    );
  }

  return (
    <div
      className={`rounded-xl border border-rose-200 dark:border-rose-800/60 bg-gradient-to-br from-rose-50 via-amber-50/40 to-white dark:from-rose-950/35 dark:via-amber-950/15 dark:to-slate-900/40 p-3.5 sm:p-4 custom-font ${className}`}
    >
      <div className="flex items-start justify-between gap-2 mb-3 flex-wrap">
        <div className="min-w-0">
          <div className="text-sm font-semibold text-rose-800 dark:text-rose-200 flex items-center gap-1.5">
            <FallOutlined />
            {title}
          </div>
          {plan.status_label ? (
            <div className="text-[11px] text-rose-600/90 dark:text-rose-300/80 mt-0.5">
              {plan.status_label}
            </div>
          ) : null}
        </div>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-2">
        <ActionPlanBox
          icon={<FallOutlined />}
          label="فاصله تا هدف"
          value={gapValue}
          emphasize
        />
        <ActionPlanBox
          icon={<CalendarOutlined />}
          label="روز کاری باقی‌مانده"
          value={daysValue}
        />
        <ActionPlanBox
          icon={<FundProjectionScreenOutlined />}
          label="فروش لازم روزانه"
          value={dailyValue}
          emphasize
        />
        <ActionPlanBox
          icon={<ShoppingOutlined />}
          label="معاملات موردنیاز"
          value={dealsValue}
        />
      </div>

      {plan.summary_text ? (
        <div className="mt-3 text-[11px] text-rose-700/85 dark:text-rose-300/80 leading-relaxed border-t border-rose-100 dark:border-rose-900/40 pt-2.5">
          {plan.summary_text}
        </div>
      ) : null}
    </div>
  );
};

export default GoalActionPlanCard;
